import type { KitRow } from "@patronix/domain";
import { coverageTone } from "@patronix/domain/aps-rules";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import { KitPanel } from "@/features/production/KitPanel";
import { atpTone, isKitStatusFilter, kitDetailQuery, kitKeys, kitListQuery, kitStatusFilters, listKits, type KitStatusFilter } from "@/features/production/kits";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";

// Production kits (APS, docs/design/aps.md): list → detail. List = one keyset
// page per request through production_kit_page (status + search; coverage %,
// ATP status and CTP date from the snapshot, K12); detail =
// production_kit_document (live ATP), prefetched on hover. Import of kit
// membership and links (ADR-034).

const toneText = {
  success: "text-(--status-success-fg)",
  warning: "text-(--status-warning-fg)",
  danger: "text-(--status-danger-fg)",
} as const;

interface Search {
  status: KitStatusFilter;
  q: string;
  kit?: string | undefined;
  import?: "production_kits" | undefined;
}

const invalidateAfterImport = [kitKeys.all, ["production", "orders"]] as const;

export const Route = createFileRoute("/_app/manufacturing/kits")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { status: isKitStatusFilter(raw.status) ? raw.status : "open", q: typeof raw.q === "string" ? raw.q : "" };
    if (typeof raw.kit === "string" && raw.kit) s.kit = raw.kit;
    if (raw.import === "production_kits") s.import = raw.import;
    return s;
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(kitListQuery({ companyId, ...deps }));
  },
  component: KitsPage,
});

function KitsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("production.order.manage");
  const canImport = useCan("core.data.import") && canManage;
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = { companyId: company?.id ?? "", status: search.status, q: search.q };
  const query = useInfiniteQuery(kitListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, kit: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<KitRow>[] = [
    {
      key: "number",
      header: t("kit.colNumber"),
      width: "minmax(130px,0.8fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>,
    },
    { key: "name", header: t("kit.name"), width: "minmax(180px,1.5fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    {
      key: "coverage",
      header: t("kit.colCoverage"),
      width: "minmax(150px,1fr)",
      cell: (r) => {
        if (r.coverage_pct === null || !r.atp_status) return <span className="text-ink-muted">—</span>;
        const pct = Number(r.coverage_pct);
        return (
          <span className="flex items-center gap-2">
            <span className={cn("tabular w-14 text-end font-medium", toneText[coverageTone(pct)])}>{fmt.pct(Math.floor(pct * 10) / 10)} %</span>
            <Badge tone={atpTone[r.atp_status]}>{t(`kit.atpStatus.${r.atp_status}`)}</Badge>
          </span>
        );
      },
    },
    {
      key: "start",
      header: t("kit.plannedStart"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <span className="tabular text-ink-muted">{fmt.day(r.planned_start)}</span>,
    },
    {
      key: "due",
      header: t("kit.plannedDue"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <span className="tabular text-ink-muted">{r.planned_due ? fmt.day(r.planned_due) : "—"}</span>,
    },
    {
      key: "ctp",
      header: t("kit.ctp"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => (
        <span className={cn("tabular", r.earliest_ctp_date && r.planned_due && r.earliest_ctp_date > r.planned_due ? "font-medium text-(--status-danger-fg)" : "text-ink-muted")}>
          {r.earliest_ctp_date ? fmt.day(r.earliest_ctp_date) : "—"}
        </span>
      ),
    },
    {
      key: "orders",
      header: t("kit.orders"),
      width: "minmax(80px,0.5fr)",
      align: "end",
      cell: (r) => <span className="tabular">{t("kit.orderCount", { open: r.open_count, total: r.order_count })}</span>,
    },
  ];
  const exportColumns: ExportColumn<KitRow>[] = [
    { header: t("kit.colNumber"), kind: "code", value: (r) => r.number },
    { header: t("kit.name"), kind: "text", value: (r) => r.name },
    { header: t("po.colStatus"), kind: "code", value: (r) => r.status },
    { header: t("kit.colCoverage"), kind: "number", value: (r) => (r.coverage_pct === null ? null : String(r.coverage_pct)), sum: false },
    { header: t("kit.colAtp"), kind: "code", value: (r) => r.atp_status },
    { header: t("kit.plannedStart"), kind: "date", value: (r) => r.planned_start },
    { header: t("kit.plannedDue"), kind: "date", value: (r) => r.planned_due },
    { header: t("kit.ctp"), kind: "date", value: (r) => r.earliest_ctp_date },
    { header: t("kit.orders"), kind: "integer", value: (r) => r.order_count },
    { header: t("project.colCode"), kind: "code", value: (r) => r.project_code },
  ];
  const listTools: ListTools<KitRow> = {
    id: "production-kits",
    title: t("kit.title"),
    onNew: canManage ? () => setSearch({ kit: "new", import: undefined }) : undefined,
    onImport: canImport ? () => setSearch({ import: "production_kits", kit: undefined }) : undefined,
    export: { fileBase: "kits", sheet: t("kit.title"), columns: exportColumns, fetchPage: (cursor, signal) => listKits(params, cursor, signal, EXPORT_PAGE) },
  };
  const label = (f: KitStatusFilter) => (f === "all" ? t("po.filterAll") : t(`kit.status.${f}`));

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.manufacturing.title")} <span aria-hidden="true">/</span> {t("nav.manufacturing.kits")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("kit.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ kit: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("kit.new")}
          </button>
        )}
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {kitStatusFilters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, kit: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                search.status === f ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("kit.searchPlaceholder")}
            aria-label={t("kit.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("kit.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.kit}
          onSelect={(r) => setSearch({ kit: r.id, import: undefined })}
          onClear={() => setSearch({ kit: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(kitDetailQuery(r.id))}
          emptyTitle={t("kit.emptyTitle")}
          emptyBody={t("kit.emptyBody")}
          emptyAction={
            canManage ? (
              <button
                type="button"
                onClick={() => setSearch({ kit: "new" })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("kit.new")}
              </button>
            ) : undefined
          }
          minWidth={900}
          tools={listTools}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("kit.detailLabel")}
          open={!!search.kit || !!search.import}
          placeholder={<DetailPlaceholder title={t("kit.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["production_kits"]}
              initial="production_kits"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.kit && (
              <KitPanel
                key={search.kit}
                id={search.kit}
                companyId={company?.id ?? ""}
                onClose={() => setSearch({ kit: undefined })}
                onOpen={(id) => setSearch({ kit: id })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
